import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ApplicationControl from './Application/ApplicationControl';
import { applicationTitle } from './Application/applicationTitle';

function AllApplication() {
    const navigate = useNavigate();
    const [percentComplete, setPercentComplete] = useState(0);
    const [studentData, setStudentData] = useState({});

    // Fetch unified user data
    const fetchUserData = async () => {
        try {
            const response = await fetch('/user-authentication-data');
            const data = await response.json();

            setStudentData(data);

            if (data.educationDetails) {
                setPercentComplete(80);
            } else if (data.personalDetails) {
                setPercentComplete(50);
            }
        } catch (error) {
            console.error('Error fetching user data:', error);
        }
    };

    useEffect(() => {
        fetchUserData();
    }, []);

    const application = async (prog) => {
        try {
            const response = await fetch(`/api/v2/application/${prog}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId: studentData._id })
            });
            const data = await response.json();

            if (data.success) {
                fetchUserData();
                // User expects navigation to /student-dashboard/{prog}-{applicationNo} instead of static fillApplication routes
                if (data.applicationNo) {
                    navigate(`/student-dashboard/${prog}-${data.applicationNo}`);
                } else {
                    // Fallback to static routes if applicationNo is missing from backend response temporarily
                    if (prog === 'mba') navigate('/fillApplication-step1');
                    else if (prog === 'engg') navigate('/online-engg-1-fillApplication');
                    else if (prog === 'pcm') navigate('/online-pcm-o-fillApplication');
                    else if (prog === 'pcb') navigate('/online-pcbo-fillApplication');
                    else if (prog === 'other') navigate('/online-all-other-program-fillApplication');
                }
            } else {
                alert(data.message);
            }
        } catch (error) {
            console.error('Error applying for program:', error);
        }
    };

    const calculatePercentage = (progData) => {
        return progData && progData.examSlot ? percentComplete + 10 : percentComplete;
    };

    return (
        <div className='paymentPage w-full px-2 sm:px-4 lg:px-6 mx-auto'>
            {/* Responsive Table Header block for Desktop - Widths deliberately matched to HTML table behavior */}
            <div className='hidden md:grid md:grid-cols-[36%_16%_16%_16%_16%] bg-white border border-gray-300 rounded-t-md text-sm font-semibold text-gray-800 shadow-sm mt-4 overflow-hidden'>
                <div className='px-4 py-3 border-r border-gray-300 flex items-center min-w-0 break-words'>Application Form</div>
                <div className='px-4 py-3 border-r border-gray-300 flex items-center justify-center text-center min-w-0 break-words'>Application No.</div>
                <div className='px-4 py-3 border-r border-gray-300 flex items-center justify-center text-center min-w-0 break-words'>Application Fees</div>
                <div className='px-4 py-3 border-r border-gray-300 flex items-center justify-center text-center min-w-0 break-words leading-tight'>Eligibility Criteria & Syllabus</div>
                <div className='px-4 py-3 flex items-center justify-center text-center min-w-0 break-words'>Action</div>
            </div>

            <div className="space-y-8 md:space-y-4 md:border md:border-t-0 border-transparent md:border-gray-300 bg-transparent md:bg-white rounded-b-md md:p-4 pb-6 mt-4 md:mt-0">

                {/* MBA */}
                <div className="bg-white p-4 md:p-0 rounded-md shadow-sm md:shadow-none border md:border-none border-gray-200">
                    <p className="font-semibold text-lg md:text-base text-gray-800 border-b md:border-b-0 pb-2 md:pb-0 mb-3 md:mb-2 px-1 md:px-0">Post Graduate Programs</p>
                    <ApplicationControl
                        percentage={calculatePercentage(studentData.mba)}
                        route="mba"
                        eligibility='https://docs.aicee.in/uploads/eligibility-criteria-for-mba.pdf'
                        title={applicationTitle.mba}
                        applicationNo={studentData.mba?.applicationNo}
                        application={application}
                        isApplicationCompleted={studentData.mba?.payStatus}
                        applicationTitle="PG (MBA/PGDM)"
                    />
                </div>

                {/* Engineering */}
                <div className="bg-white p-4 md:p-0 rounded-md shadow-sm md:shadow-none border md:border-none border-gray-200">
                    <p className="font-semibold text-lg md:text-base text-gray-800 border-b md:border-b-0 pb-2 md:pb-0 mb-3 md:mb-2 px-1 md:px-0">Under Graduate Programs</p>
                    <ApplicationControl
                        percentage={calculatePercentage(studentData.engg)}
                        route="engg"
                        eligibility='https://docs.aicee.in/uploads/eligibility-criteria-for-engineering.pdf'
                        title={applicationTitle.engg}
                        applicationNo={studentData.engg?.applicationNo}
                        application={application}
                        isApplicationCompleted={studentData.engg?.payStatus}
                        applicationTitle="Engineering UG"
                    />
                </div>

                {/* PCM */}
                <div className="bg-white p-4 md:p-0 rounded-md shadow-sm md:shadow-none border md:border-none border-gray-200">
                    <p className="font-semibold text-lg md:text-base text-gray-800 border-b md:border-b-0 pb-2 md:pb-0 mb-3 md:mb-2 px-1 md:px-0 flex flex-wrap items-center gap-2">
                        Under Graduate <span className='text-xs md:text-sm text-white bg-[#042e2e] font-bold px-2 py-0.5 rounded-full'>(PCM Students only)</span>
                    </p>
                    <ApplicationControl
                        percentage={calculatePercentage(studentData.pcm)}
                        route="pcm"
                        eligibility='https://docs.aicee.in/uploads/eligibility-criteria-for-12th-level-pcb-programs.pdf'
                        title={applicationTitle.pcm}
                        applicationNo={studentData.pcm?.applicationNo}
                        application={application}
                        isApplicationCompleted={studentData.pcm?.payStatus}
                        applicationTitle="PCB UG"
                    />
                </div>

                {/* PCB */}
                <div className="bg-white p-4 md:p-0 rounded-md shadow-sm md:shadow-none border md:border-none border-gray-200">
                    <p className="font-semibold text-lg md:text-base text-gray-800 border-b md:border-b-0 pb-2 md:pb-0 mb-3 md:mb-2 px-1 md:px-0 flex flex-wrap items-center gap-2">
                        Under Graduate <span className='text-xs md:text-sm text-white bg-[#042e2e] font-bold px-2 py-0.5 rounded-full'>(PCB Students only)</span>
                    </p>
                    <ApplicationControl
                        percentage={calculatePercentage(studentData.pcb)}
                        route="pcb"
                        eligibility='https://docs.aicee.in/uploads/eligibility-criteria-for-12th-level-pcm-programs.pdf'
                        title={applicationTitle.pcb}
                        applicationNo={studentData.pcb?.applicationNo}
                        application={application}
                        isApplicationCompleted={studentData.pcb?.payStatus}
                        applicationTitle="PCM UG"
                    />
                </div>

                {/* Other */}
                <div className="bg-white p-4 md:p-0 rounded-md shadow-sm md:shadow-none border md:border-none border-gray-200">
                    <p className="font-semibold text-lg md:text-base text-gray-800 border-b md:border-b-0 pb-2 md:pb-0 mb-3 md:mb-2 px-1 md:px-0 flex flex-wrap items-center gap-2">
                        Under Graduate <span className='text-xs md:text-sm text-white bg-[#042e2e] font-bold px-2 py-0.5 rounded-full'>(All Other Program)</span>
                    </p>
                    <ApplicationControl
                        percentage={calculatePercentage(studentData.other)}
                        route="other"
                        eligibility='https://docs.aicee.in/uploads/eligibility-criteria-other-12th-level--other-programs.pdf'
                        title={applicationTitle.other}
                        applicationNo={studentData.other?.applicationNo}
                        application={application}
                        isApplicationCompleted={studentData.other?.payStatus}
                        applicationTitle="Other UG"
                    />
                </div>
            </div>
        </div>
    );
}

export default AllApplication;